import { useCart } from '../contexts/CartContext'
import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { geocodeAddress, computeDistanceMiles } from '../utils/location'
import { storeConfig } from '../utils/config'

function CartPage() {
  const { 
    items, 
    shippingOption, 
    setShippingOption, 
    updateQuantity, 
    removeFromCart, 
    getCartTotals,
    clearCart,
    setShippingAddress,
    setBillingAddress
  } = useCart()
  const navigate = useNavigate()

  const [isLocalShippingOpen, setIsLocalShippingOpen] = useState(true)
  const [isStandardShippingOpen, setIsStandardShippingOpen] = useState(true)
  const [showFreeShippingModal, setShowFreeShippingModal] = useState(false)
  const [showDeliveryModal, setShowDeliveryModal] = useState(false)
  const [enableAutoDeliveryCheck, setEnableAutoDeliveryCheck] = useState(false)
  const [deliveryEligibleVisual, setDeliveryEligibleVisual] = useState(false)

  const [address, setAddress] = useState({
    fullName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: ''
  })
  const [addressErrors, setAddressErrors] = useState({})

  const [localAddress, setLocalAddress] = useState({
    fullName: '',
    address1: '',
    address2: '',
    city: '',
    state: 'TX',
    zip: ''
  })
  const [localErrors, setLocalErrors] = useState({})
  const [deliveryCheck, setDeliveryCheck] = useState({ checking: false, eligible: null, distance: null, message: '' })

  const totals = getCartTotals()

  useEffect(() => {
    if (shippingOption === 'delivery') {
      setIsLocalShippingOpen(true)
    }
    if (shippingOption === 'shipping') {
      setIsStandardShippingOpen(true)
    }
  }, [shippingOption])

  if (items.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <h1 className="cart-title">My Bag</h1>
          <div className="empty-cart">
            {/* Removed empty cart icon */}
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added any items to your cart yet.</p>
            <Link to="/shop" className="continue-shopping-btn">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const isLocalSelected = shippingOption === 'pickup' || shippingOption === 'delivery'
  const isStandardSelected = shippingOption === 'shipping'

  const validateAddress = () => {
    const errors = {}
    if (!address.fullName.trim()) errors.fullName = 'Required'
    if (!address.address1.trim()) errors.address1 = 'Required'
    if (!address.city.trim()) errors.city = 'Required'
    if (!address.state.trim()) errors.state = 'Required'
    if (!address.zip.trim()) errors.zip = 'Required'
    if (address.zip && !/^\d{5}(-\d{4})?$/.test(address.zip)) errors.zip = 'Invalid ZIP'
    setAddressErrors(errors)
    return Object.keys(errors).length === 0
  }

  const validateLocalAddress = () => {
    const errors = {}
    if (!localAddress.fullName.trim()) errors.fullName = 'Required'
    if (!localAddress.address1.trim()) errors.address1 = 'Required'
    if (!localAddress.city.trim()) errors.city = 'Required'
    if (!localAddress.state.trim()) errors.state = 'Required'
    if (!localAddress.zip.trim()) errors.zip = 'Required'
    if (localAddress.zip && !/^\d{5}(-\d{4})?$/.test(localAddress.zip)) errors.zip = 'Invalid ZIP'
    setLocalErrors(errors)
    return Object.keys(errors).length === 0
  }

  const checkLocalDeliveryEligibility = async () => {
    if (!validateLocalAddress()) {
      setDeliveryCheck({ checking: false, eligible: null, distance: null, message: 'Please correct the highlighted fields.' })
      return false
    }
    setDeliveryCheck({ checking: true, eligible: null, distance: null, message: 'Checking delivery radius…' })

    const origin = storeConfig.originCoords || await geocodeAddress(storeConfig.originAddress)
    const destAddress = `${localAddress.address1}, ${localAddress.city}, ${localAddress.state} ${localAddress.zip}`
    const dest = await geocodeAddress(destAddress)

    if (!origin || !dest) {
      setDeliveryCheck({ checking: false, eligible: null, distance: null, message: 'Could not verify location. Please try standard shipping or try again.' })
      return false
    }

    const distance = computeDistanceMiles(origin, dest)
    const eligible = distance !== null && distance <= storeConfig.deliveryRadiusMiles

    setDeliveryCheck({ checking: false, eligible, distance, message: eligible ? `Eligible: ${distance.toFixed(1)} miles away` : `Outside radius: ${distance.toFixed(1)} miles (max ${storeConfig.deliveryRadiusMiles})` })

    if (!eligible) {
      // Force switch to standard shipping
      setShippingOption('shipping')
      setIsStandardShippingOpen(true)
    }

    return eligible
  }

  // Optionally auto-check eligibility when enabled (kept off until user submits in modal)
  useEffect(() => {
    if (shippingOption === 'delivery' && enableAutoDeliveryCheck) {
      checkLocalDeliveryEligibility()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingOption, enableAutoDeliveryCheck, localAddress.fullName, localAddress.address1, localAddress.city, localAddress.state, localAddress.zip])

  const handleCheckout = async () => {
    // Check if cart is under $100 and not Local Pickup
    const subtotal = parseFloat(totals.subtotal)
    if (subtotal < 100 && shippingOption !== 'pickup') {
      setShowFreeShippingModal(true)
      return
    }

    if (shippingOption === 'shipping') {
      if (!validateAddress()) {
        setIsStandardShippingOpen(true)
        return
      }
      // Save shipping address to context
      setShippingAddress(address)
    }
    if (shippingOption === 'delivery') {
      const ok = await checkLocalDeliveryEligibility()
      if (!ok) return
      // Save local delivery address to context
      setShippingAddress(localAddress)
    }
    navigate('/checkout')
  }

  const handleModalContinue = () => {
    setShowFreeShippingModal(false)
    // Proceed with checkout
    navigate('/checkout')
  }

  const handleModalAddMore = () => {
    setShowFreeShippingModal(false)
    // Scroll to top or focus on products
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="cart-page">
      <div className="container">
        <h1 className="cart-title">My Bag</h1>
        
        <div className="cart-content">
          <div className="cart-items">
            {items.map((item) => (
              <div key={item.cartId} className="cart-item">
                <div className="cart-item-image">
                  <img src={item.image} alt={item.title} />
                </div>
                
                <div className="cart-item-details">
                  <h3 className="cart-item-title">{item.title}</h3>
                  <div className="cart-item-meta">
                    <span className="cart-item-size">Size: {item.size}</span>
                    {item.color && <span className="cart-item-color">Color: {item.color}</span>}
                  </div>
                </div>
                
                <div className="cart-item-controls">
                  <div className="quantity-controls">
                    <button 
                      onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                      className="quantity-btn"
                    >
                      −
                    </button>
                    <span className="quantity">{item.quantity}</span>
                    <button 
                      onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                      className="quantity-btn"
                    >
                      +
                    </button>
                  </div>
                  
                  <button 
                    className="remove-btn"
                    onClick={() => removeFromCart(item.cartId)}
                  >
                    Remove
                  </button>
                </div>
                
                <div className="cart-item-total">
                  ${(item.price * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <div className="shipping-options">
              <h3>Shipping Options</h3>
              
              <div className={`shipping-group ${isLocalSelected ? 'selected' : ''}`}>
                <h4 
                  className="shipping-group-header"
                  onClick={() => setIsLocalShippingOpen(!isLocalShippingOpen)}
                >
                  Local Shipping - Lubbock TX
                  <span className="shipping-toggle-icon">
                    {isLocalShippingOpen ? '−' : '+'}
                  </span>
                </h4>
                {isLocalShippingOpen && (
                  <div className="shipping-group-content">
                    <button
                      type="button"
                      className={`shipping-select ${shippingOption === 'pickup' ? 'selected' : ''}`}
                      onClick={() => setShippingOption('pickup')}
                    >
                      <div className="shipping-label">
                        <strong>Local Pickup</strong>
                        <small>Free - within 20 miles of 79415</small>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`shipping-select ${shippingOption === 'delivery' ? 'selected' : ''}`}
                      onClick={() => { setShippingOption('delivery'); setShowDeliveryModal(true); }}
                    >
                      <div className="shipping-label">
                        <strong>Local Delivery</strong>
                        <small>$5.00 - within 20 miles of 79415</small>
                      </div>
                    </button>

                    {false}
                  </div>
                )}
              </div>

              <div className={`shipping-group ${isStandardSelected ? 'selected' : ''}`}>
                <h4 
                  className="shipping-group-header"
                  onClick={() => setIsStandardShippingOpen(!isStandardShippingOpen)}
                >
                  Standard Shipping
                  <span className="shipping-toggle-icon">
                    {isStandardShippingOpen ? '−' : '+'}
                  </span>
                </h4>
                {isStandardShippingOpen && (
                  <div className="shipping-group-content">
                    <button
                      type="button"
                      className={`shipping-select ${shippingOption === 'shipping' ? 'selected' : ''}`}
                      onClick={() => setShippingOption('shipping')}
                    >
                      <div className="shipping-label">
                        <strong>Shipping</strong>
                        <small>$8.00 - standard shipping</small>
                      </div>
                    </button>

                    {shippingOption === 'shipping' && (
                      <div className="shipping-address-form">
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="fullName">Full Name</label>
                            <input
                              id="fullName"
                              type="text"
                              value={address.fullName}
                              onChange={(e) => setAddress({ ...address, fullName: e.target.value })}
                              placeholder="John Doe"
                              className={addressErrors.fullName ? 'error' : ''}
                            />
                            {addressErrors.fullName && <span className="error-text">{addressErrors.fullName}</span>}
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="address1">Address</label>
                            <input
                              id="address1"
                              type="text"
                              value={address.address1}
                              onChange={(e) => setAddress({ ...address, address1: e.target.value })}
                              placeholder="123 Main St"
                              className={addressErrors.address1 ? 'error' : ''}
                            />
                            {addressErrors.address1 && <span className="error-text">{addressErrors.address1}</span>}
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="address2">Apt, suite, etc. (optional)</label>
                            <input
                              id="address2"
                              type="text"
                              value={address.address2}
                              onChange={(e) => setAddress({ ...address, address2: e.target.value })}
                              placeholder="Apt 4B"
                            />
                          </div>
                        </div>
                        <div className="form-row two-col">
                          <div className="form-group">
                            <label htmlFor="city">City</label>
                            <input
                              id="city"
                              type="text"
                              value={address.city}
                              onChange={(e) => setAddress({ ...address, city: e.target.value })}
                              placeholder="Lubbock"
                              className={addressErrors.city ? 'error' : ''}
                            />
                            {addressErrors.city && <span className="error-text">{addressErrors.city}</span>}
                          </div>
                          <div className="form-group">
                            <label htmlFor="state">State</label>
                            <select
                              id="state"
                              value={address.state}
                              onChange={(e) => setAddress({ ...address, state: e.target.value })}
                              className={addressErrors.state ? 'error' : ''}
                            >
                              <option value="">Select</option>
                              <option value="TX">TX</option>
                              <option value="NM">NM</option>
                              <option value="OK">OK</option>
                              <option value="CO">CO</option>
                              <option value="AZ">AZ</option>
                              <option value="CA">CA</option>
                              <option value="NY">NY</option>
                              <option value="FL">FL</option>
                              <option value="IL">IL</option>
                              <option value="WA">WA</option>
                            </select>
                            {addressErrors.state && <span className="error-text">{addressErrors.state}</span>}
                          </div>
                        </div>
                        <div className="form-row two-col">
                          <div className="form-group">
                            <label htmlFor="zip">ZIP Code</label>
                            <input
                              id="zip"
                              type="text"
                              value={address.zip}
                              onChange={(e) => setAddress({ ...address, zip: e.target.value })}
                              placeholder="79401"
                              className={addressErrors.zip ? 'error' : ''}
                            />
                            {addressErrors.zip && <span className="error-text">{addressErrors.zip}</span>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="order-summary">
              <h3>Order Summary</h3>
              <div className="summary-line">
                <span>Subtotal:</span>
                <span>${totals.subtotal}</span>
              </div>
              <div className="summary-line">
                <span>Shipping ({shippingOption === 'delivery' ? 'Local Delivery' : shippingOption === 'shipping' ? 'Shipping' : 'Local Pickup'}):</span>
                <span>${totals.shipping}</span>
              </div>
              <div className="summary-line">
                <span>Tax (8.25%):</span>
                <span>${totals.tax}</span>
              </div>
              <div className="summary-line total">
                <span>Total:</span>
                <span>${totals.total}</span>
              </div>
            </div>

            <div className="cart-actions">
              <button className="checkout-btn" onClick={handleCheckout}>Proceed to Checkout</button>
              <button className="clear-cart-btn" onClick={clearCart}>Clear Cart</button>
            </div>
          </div>
        </div>
      </div>

      {/* Free Shipping Modal */}
      {showFreeShippingModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Free Shipping Available!</h2>
            <p>Add ${(100 - parseFloat(totals.subtotal)).toFixed(2)} more to your order and get free shipping on orders over $100.</p>
            <div className="modal-actions">
              <button className="modal-btn secondary" onClick={handleModalContinue}>
                Continue to Checkout
              </button>
              <button className="modal-btn primary" onClick={handleModalAddMore}>
                Add More Items
              </button>
            </div>
            <button 
              className="modal-close" 
              onClick={() => setShowFreeShippingModal(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Local Delivery Eligibility Modal */}
      {showDeliveryModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Let's check your eligibility for local delivery</h2>
            <div className="shipping-address-form" style={{ marginTop: '1rem' }}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ld_fullName">Full Name</label>
                  <input
                    id="ld_fullName"
                    type="text"
                    value={localAddress.fullName}
                    onChange={(e) => setLocalAddress({ ...localAddress, fullName: e.target.value })}
                    placeholder="John Doe"
                    className={localErrors.fullName ? 'error' : ''}
                  />
                  {localErrors.fullName && <span className="error-text">{localErrors.fullName}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ld_address1">Address</label>
                  <input
                    id="ld_address1"
                    type="text"
                    value={localAddress.address1}
                    onChange={(e) => setLocalAddress({ ...localAddress, address1: e.target.value })}
                    placeholder="123 Main St"
                    className={localErrors.address1 ? 'error' : ''}
                  />
                  {localErrors.address1 && <span className="error-text">{localErrors.address1}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ld_address2">Apt, suite, etc. (optional)</label>
                  <input
                    id="ld_address2"
                    type="text"
                    value={localAddress.address2}
                    onChange={(e) => setLocalAddress({ ...localAddress, address2: e.target.value })}
                    placeholder="Apt 4B"
                  />
                </div>
              </div>
              <div className="form-row two-col">
                <div className="form-group">
                  <label htmlFor="ld_city">City</label>
                  <input
                    id="ld_city"
                    type="text"
                    value={localAddress.city}
                    onChange={(e) => setLocalAddress({ ...localAddress, city: e.target.value })}
                    placeholder="Lubbock"
                    className={localErrors.city ? 'error' : ''}
                  />
                  {localErrors.city && <span className="error-text">{localErrors.city}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="ld_state">State</label>
                  <select
                    id="ld_state"
                    value={localAddress.state}
                    onChange={(e) => setLocalAddress({ ...localAddress, state: e.target.value })}
                    className={localErrors.state ? 'error' : ''}
                  >
                    <option value="">Select</option>
                    <option value="TX">TX</option>
                    <option value="NM">NM</option>
                    <option value="OK">OK</option>
                    <option value="CO">CO</option>
                    <option value="AZ">AZ</option>
                    <option value="CA">CA</option>
                    <option value="NY">NY</option>
                    <option value="FL">FL</option>
                    <option value="IL">IL</option>
                    <option value="WA">WA</option>
                  </select>
                  {localErrors.state && <span className="error-text">{localErrors.state}</span>}
                </div>
              </div>
              <div className="form-row two-col">
                <div className="form-group">
                  <label htmlFor="ld_zip">ZIP Code</label>
                  <input
                    id="ld_zip"
                    type="text"
                    value={localAddress.zip}
                    onChange={(e) => setLocalAddress({ ...localAddress, zip: e.target.value })}
                    placeholder="79401"
                    className={localErrors.zip ? 'error' : ''}
                  />
                  {localErrors.zip && <span className="error-text">{localErrors.zip}</span>}
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="modal-btn primary" 
                  onClick={async () => { 
                    const ok = await checkLocalDeliveryEligibility(); 
                    if (ok) { 
                      setDeliveryEligibleVisual(true)
                      setTimeout(() => {
                        setEnableAutoDeliveryCheck(true)
                        setShowDeliveryModal(false)
                        setDeliveryEligibleVisual(false)
                      }, 1100)
                    }
                  }}
                  disabled={deliveryCheck.checking}
                >
                  {deliveryCheck.checking ? 'Checking…' : 'Check Eligibility'}
                </button>
                <button 
                  type="button" 
                  className="modal-btn secondary" 
                  onClick={() => { setShowDeliveryModal(false); }}
                >
                  Cancel
                </button>
              </div>
              {deliveryEligibleVisual && (
                <div className="eligibility-success" role="status" aria-live="polite">
                  <span className="checkmark">✓</span>
                  <span>Eligible for Local Delivery</span>
                </div>
              )}
              {deliveryCheck.message && (
                <div className="form-row">
                  <small style={{ color: deliveryCheck.eligible === false ? '#b91c1c' : '#065f46' }}>
                    {deliveryCheck.message}
                  </small>
                </div>
              )}
            </div>
            <button 
              className="modal-close" 
              onClick={() => setShowDeliveryModal(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CartPage
